import React from 'react';
import { Box, Container, Typography, Breadcrumbs, Link, Paper } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MainLayout from './MainLayout';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  showBreadcrumbs?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  children, 
  title,
  subtitle,
  maxWidth = 'xl',
  showBreadcrumbs = true
}) => {
  const location = useLocation();

  // Generate breadcrumbs based on current path
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [
      { label: 'Home', path: '/' },
      { label: 'Admin', path: '/admin' }
    ];

    // Add specific admin page breadcrumbs
    if (pathSegments.length > 1) {
      const adminPage = pathSegments[1];
      switch (adminPage) {
        case 'products':
          breadcrumbs.push({ label: 'Products', path: '/admin/products' });
          break;
        case 'orders':
          breadcrumbs.push({ label: 'Orders', path: '/admin/orders' });
          if (pathSegments[2]) {
            breadcrumbs.push({ label: `Order #${pathSegments[2]}`, path: location.pathname });
          }
          break;
        case 'themes':
          breadcrumbs.push({ label: 'Themes', path: '/admin/themes' });
          break;
        case 'webhooks':
          breadcrumbs.push({ label: 'Webhooks', path: '/admin/webhooks' });
          break;
        case 'errors':
          breadcrumbs.push({ label: 'Error Logs', path: '/admin/errors' });
          break;
        default:
          break;
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <MainLayout backgroundVariant="light" backgroundDensity="low">
      <Container maxWidth={maxWidth} sx={{ py: 2 }}>
        {/* Admin Header */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: 3, 
            mb: 3, 
            background: 'linear-gradient(135deg, rgba(42, 60, 52, 0.05) 0%, rgba(217, 165, 102, 0.05) 100%)',
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main'
          }}
        >
          {showBreadcrumbs && (
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: title ? 2 : 0 }}
              aria-label="admin navigation breadcrumbs"
            >
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <Typography key={crumb.path} color="text.primary" variant="body2">
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.path}
                    component={RouterLink}
                    to={crumb.path}
                    color="inherit"
                    variant="body2"
                    underline="hover"
                  >
                    {crumb.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}

          {title && (
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom={!!subtitle}
              sx={{ 
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              {title}
            </Typography>
          )}

          {subtitle && (
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ mb: 0 }}
            >
              {subtitle}
            </Typography>
          )}
        </Paper>

        {/* Admin Content */}
        <Box sx={{ minHeight: '60vh' }}>
          {children}
        </Box>
      </Container>
    </MainLayout>
  );
};

export default AdminLayout; 